import { useEffect, useState, useCallback } from "react";
import { financialsAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell
} from "recharts";
import { Download, RefreshCw, CheckCircle, DollarSign, TrendingUp, Percent } from "lucide-react";
import { format } from "date-fns";
import toast from "react-hot-toast";

const PERIODS = ["today","week","month","year"];
const COLORS   = ["#f5a623","#22c55e","#3b82f6","#a855f7","#ff6b35"];

const fmt = (n) => parseFloat(n || 0).toLocaleString("en-GH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:8, padding:"10px 14px" }}>
      <p style={{ color:"var(--text2)", fontSize:11, marginBottom:4 }}>{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ color:p.color, fontSize:13, fontWeight:600 }}>
          {p.name}: GHS {fmt(p.value)}
        </p>
      ))}
    </div>
  );
};

export default function Financials() {
  const { user } = useAuth();
  const isSuperOrAdmin = ["super_admin","admin"].includes(user?.role);

  const [data, setData]       = useState(null);
  const [period, setPeriod]   = useState("month");
  const [from, setFrom]       = useState("");
  const [to, setTo]           = useState("");
  const [loading, setLoading] = useState(false);
  const [payoutModal, setPayoutModal] = useState(null);
  const [payoutAmt, setPayoutAmt]     = useState("");
  const [payoutNote, setPayoutNote]   = useState("");
  const [paying, setPaying]           = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = from && to ? { from, to } : { period };
      const r = await financialsAPI.get(params);
      setData(r.data.data);
    } catch { toast.error("Failed to load financials"); }
    finally { setLoading(false); }
  }, [period, from, to]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async (type) => {
    try {
      const params = from && to ? { from, to, type } : { period, type };
      const r = await financialsAPI.exportCSV(params);
      const url  = URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `quickie-${type}-${Date.now()}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const handlePayout = async () => {
    if (!payoutAmt || !payoutModal) return;
    setPaying(true);
    try {
      await financialsAPI.payout({
        rider_id: payoutModal.rider_id,
        amount:   parseFloat(payoutAmt),
        note:     payoutNote,
      });
      toast.success(`Payout of GHS ${payoutAmt} recorded for ${payoutModal.rider_name}`);
      setPayoutModal(null);
      setPayoutAmt("");
      setPayoutNote("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Payout failed");
    } finally {
      setPaying(false);
    }
  };

  const rev      = data?.revenue || {};
  const daily    = (data?.daily || []).map(d => ({
    date:         format(new Date(d.date), "MMM d"),
    "Gross":      parseFloat(d.gross_revenue || 0),
    "Platform":   parseFloat(d.platform_fee  || 0),
    "Rider Cut":  parseFloat(d.rider_payouts || 0),
  }));
  const byService = (data?.by_service || []).map(s => ({
    name:    s.service_type,
    revenue: parseFloat(s.revenue || 0),
    orders:  s.orders,
  }));
  const byBranch = data?.by_branch || [];
  const riders   = data?.rider_payouts || [];
  const promos   = data?.promo_costs  || [];

  const defaultPct  = data?.default_rider_commission_pct || "20%";
  const platformPct = data ? `${(100 - parseFloat(defaultPct)).toFixed(0)}%` : "80%";

  return (
    <div>
      <style>{`
        .fin-service-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 16px;
        }
        .fin-filters-row {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
        }
        .fin-period-btns {
          display: flex;
          gap: 4px;
          background: var(--bg3);
          padding: 4px;
          border-radius: 10px;
          flex-shrink: 0;
        }
        .fin-date-row {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
        }
        .rider-mobile-card {
          padding: 14px 16px;
          border-bottom: 1px solid var(--border);
        }
        .rider-mobile-card:last-child { border-bottom: none; }
        .fin-mobile-cards   { display: none; }
        .fin-desktop-table  { display: block; }
        .fin-header-actions { display: flex; gap: 8px; flex-wrap: wrap; }
        @media (max-width: 900px) {
          .fin-service-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 768px) {
          .fin-mobile-cards   { display: block; }
          .fin-desktop-table  { display: none; }
        }
        @media (max-width: 600px) {
          .fin-header-actions .btn span { display: none; }
        }
      `}</style>

      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">Financials</div>
          <div className="page-subtitle">
            {data?.scope === "branch" ? "Your branch revenue" : "Platform-wide financial overview"}
          </div>
        </div>
        <div className="fin-header-actions">
          <button className="btn btn-ghost btn-sm" onClick={() => handleExport("orders")}>
            <Download size={14}/> <span>Orders CSV</span>
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => handleExport("payouts")}>
            <Download size={14}/> <span>Payouts CSV</span>
          </button>
          <button className="btn btn-ghost btn-sm" onClick={load}>
            <RefreshCw size={14}/>
          </button>
        </div>
      </div>

      {/* Period selector */}
      <div className="fin-filters-row" style={{ marginBottom: 20 }}>
        <div className="fin-period-btns">
          {PERIODS.map(p => (
            <button
              key={p}
              className="btn btn-sm"
              onClick={() => { setPeriod(p); setFrom(""); setTo(""); }}
              style={{
                background: period === p && !from ? "var(--accent)" : "transparent",
                color:      period === p && !from ? "#000" : "var(--text2)",
                border: "none",
                textTransform: "capitalize",
              }}
            >{p}</button>
          ))}
        </div>
        <div className="fin-date-row">
          <input type="date" className="input" style={{ width:130 }} value={from} onChange={e => setFrom(e.target.value)}/>
          <span style={{ color:"var(--text3)" }}>to</span>
          <input type="date" className="input" style={{ width:130 }} value={to}   onChange={e => setTo(e.target.value)}/>
          {from && to && <button className="btn btn-primary btn-sm" onClick={load}>Apply</button>}
          {from && <button className="btn btn-ghost btn-sm" onClick={() => { setFrom(""); setTo(""); }}>Clear</button>}
        </div>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner"/><span>Loading financials...</span></div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="stats-grid" style={{ marginBottom: 20 }}>
            <div className="stat-card green">
              <div className="stat-label">Gross Revenue</div>
              <div className="stat-value" style={{ fontSize: 22 }}>GHS {fmt(rev.gross_revenue)}</div>
              <div className="stat-sub">{rev.total_orders} total orders</div>
              <TrendingUp size={40} className="stat-icon"/>
            </div>
            <div className="stat-card accent">
              <div className="stat-label">Platform Fee ({platformPct})</div>
              <div className="stat-value" style={{ fontSize: 22 }}>GHS {fmt(rev.platform_fee)}</div>
              <div className="stat-sub">Net platform income</div>
              <Percent size={40} className="stat-icon"/>
            </div>
            <div className="stat-card blue">
              <div className="stat-label">Rider Payouts ({defaultPct})</div>
              <div className="stat-value" style={{ fontSize: 22 }}>GHS {fmt(rev.rider_payouts)}</div>
              <div className="stat-sub">Owed to all riders</div>
              <DollarSign size={40} className="stat-icon"/>
            </div>
          </div>

          {/* Daily chart */}
          {daily.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header"><span className="card-title">Revenue Breakdown (Daily)</span></div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={daily}>
                    <defs>
                      <linearGradient id="gross" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3}/>
                        <stop offset="100%" stopColor="#22c55e" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="platform" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f5a623" stopOpacity={0.3}/>
                        <stop offset="100%" stopColor="#f5a623" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="rider" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3}/>
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fill:"var(--text3)", fontSize:11 }} axisLine={false} tickLine={false}/>
                    <YAxis tick={{ fill:"var(--text3)", fontSize:11 }} axisLine={false} tickLine={false}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <Area type="monotone" dataKey="Gross"     stroke="#22c55e" strokeWidth={2} fill="url(#gross)"/>
                    <Area type="monotone" dataKey="Platform"  stroke="#f5a623" strokeWidth={2} fill="url(#platform)"/>
                    <Area type="monotone" dataKey="Rider Cut" stroke="#3b82f6" strokeWidth={2} fill="url(#rider)"/>
                  </AreaChart>
                </ResponsiveContainer>
                <div style={{ display:"flex", gap:12, justifyContent:"center", marginTop:8, fontSize:12, flexWrap:"wrap" }}>
                  {[["Gross Revenue","#22c55e"],["Platform Fee","#f5a623"],["Rider Payouts","#3b82f6"]].map(([l,c]) => (
                    <div key={l} style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ width:10, height:10, borderRadius:2, background:c, display:"inline-block"}}/>
                      <span style={{ color:"var(--text2)" }}>{l}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="fin-service-grid">
            {byService.length > 0 && (
              <div className="card">
                <div className="card-header"><span className="card-title">Revenue by Service</span></div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={byService} barSize={32}>
                      <XAxis dataKey="name" tick={{ fill:"var(--text3)", fontSize:11, textTransform:"capitalize" }} axisLine={false} tickLine={false}/>
                      <YAxis tick={{ fill:"var(--text3)", fontSize:11 }} axisLine={false} tickLine={false}/>
                      <Tooltip contentStyle={{ background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:8 }}/>
                      <Bar dataKey="revenue" radius={[6,6,0,0]}>
                        {byService.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {isSuperOrAdmin && byBranch.length > 0 && (
              <div className="card">
                <div className="card-header"><span className="card-title">Revenue by Branch</span></div>
                {/* Mobile cards */}
                <div className="fin-mobile-cards">
                  {byBranch.map((b, i) => (
                    <div key={i} style={{padding:"12px 16px",borderBottom:"1px solid var(--border)"}}>
                      <div style={{fontWeight:700,marginBottom:4}}>{b.branch_name}</div>
                      <div style={{display:"flex",gap:16,fontSize:12}}>
                        <span style={{color:"var(--text3)"}}>{b.orders} orders</span>
                        <span style={{color:"var(--green)",fontWeight:700}}>GHS {fmt(b.revenue)}</span>
                        <span style={{color:"var(--accent)"}}>Fee: GHS {fmt(b.platform_fee)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop table */}
                <div className="fin-desktop-table table-wrap">
                  <table>
                    <thead><tr><th>Branch</th><th>Orders</th><th>Revenue</th><th>Platform Fee</th></tr></thead>
                    <tbody>
                      {byBranch.map((b, i) => (
                        <tr key={i}>
                          <td className="fw-bold">{b.branch_name}</td>
                          <td className="mono">{b.orders}</td>
                          <td className="mono" style={{ color:"var(--green)", fontWeight:700 }}>GHS {fmt(b.revenue)}</td>
                          <td className="mono" style={{ color:"var(--accent)" }}>GHS {fmt(b.platform_fee)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Rider payouts */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <span className="card-title">💳 Rider Payout Summary</span>
              <span style={{ fontSize:12, color:"var(--text3)" }}>
                {defaultPct} rider / {platformPct} platform
              </span>
            </div>

            {/* Mobile payout cards */}
            <div className="fin-mobile-cards">
              {riders.length === 0
                ? <div className="empty-state"><p>No payout data</p></div>
                : riders.map(r => (
                <div key={r.rider_id} className="rider-mobile-card">
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                    <div>
                      <div style={{fontWeight:700,fontSize:14}}>{r.rider_name}</div>
                      <div style={{fontSize:11,color:"var(--text3)"}}>{r.phone} · {r.vehicle_type} · {r.commission_pct} · {r.deliveries} deliveries</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{color:"var(--green)",fontWeight:700,fontSize:13}}>GHS {fmt(r.total_earned)}</div>
                      <div style={{color:parseFloat(r.pending_payout)>0?"var(--accent)":"var(--text3)",fontSize:12}}>
                        Pending: GHS {fmt(r.pending_payout)}
                      </div>
                    </div>
                  </div>
                  {isSuperOrAdmin && parseFloat(r.pending_payout) > 0 && (
                    <button
                      className="btn btn-success btn-sm"
                      style={{width:"100%",justifyContent:"center",marginTop:4}}
                      onClick={() => { setPayoutModal(r); setPayoutAmt(parseFloat(r.pending_payout).toFixed(2)); }}
                    >
                      Mark Paid
                    </button>
                  )}
                  {isSuperOrAdmin && parseFloat(r.pending_payout) <= 0 && (
                    <div style={{fontSize:12,color:"var(--text3)",marginTop:4}}>✓ Fully paid</div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop payout table */}
            <div className="fin-desktop-table table-wrap">
              <table>
                <thead><tr>
                  <th>Rider</th><th>Vehicle</th><th>Rate</th><th>Deliveries</th>
                  <th>Total Earned</th><th>Pending Payout</th>
                  {isSuperOrAdmin && <th>Action</th>}
                </tr></thead>
                <tbody>
                  {riders.length === 0
                    ? <tr><td colSpan={7}><div className="empty-state"><p>No payout data</p></div></td></tr>
                    : riders.map((r) => (
                    <tr key={r.rider_id}>
                      <td>
                        <div className="fw-bold">{r.rider_name}</div>
                        <div className="mono" style={{ color:"var(--text3)", fontSize:11 }}>{r.phone}</div>
                      </td>
                      <td style={{ textTransform:"capitalize", color:"var(--text2)" }}>{r.vehicle_type}</td>
                      <td><span style={{ fontSize:12, fontWeight:700, color:"var(--text2)" }}>{r.commission_pct}</span></td>
                      <td className="mono">{r.deliveries}</td>
                      <td className="mono" style={{ color:"var(--green)", fontWeight:700 }}>GHS {fmt(r.total_earned)}</td>
                      <td>
                        <span style={{
                          color: parseFloat(r.pending_payout) > 0 ? "var(--accent)" : "var(--text3)",
                          fontFamily:"DM Mono", fontSize:13, fontWeight:700
                        }}>
                          GHS {fmt(r.pending_payout)}
                        </span>
                      </td>
                      {isSuperOrAdmin && (
                        <td>
                          {parseFloat(r.pending_payout) > 0 ? (
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => { setPayoutModal(r); setPayoutAmt(parseFloat(r.pending_payout).toFixed(2)); }}
                            >
                              Mark Paid
                            </button>
                          ) : (
                            <span style={{ fontSize:12, color:"var(--text3)" }}>✓ Paid</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Promo cost tracking */}
          {promos.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header"><span className="card-title">🏷️ Promo Code Cost</span></div>

              {/* Mobile */}
              <div className="fin-mobile-cards">
                {promos.map((p, i) => (
                  <div key={i} style={{padding:"12px 16px",borderBottom:"1px solid var(--border)"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div>
                        <span className="mono" style={{color:"var(--accent)",fontWeight:700,fontSize:13}}>{p.code}</span>
                        <span style={{marginLeft:8,fontSize:12,color:"var(--text3)",textTransform:"capitalize"}}>{p.type}</span>
                      </div>
                      <span className="mono" style={{color:"var(--red)",fontWeight:700}}>- GHS {fmt(p.total_discount)}</span>
                    </div>
                    <div style={{fontSize:12,color:"var(--text3)",marginTop:4}}>
                      {p.type==="percentage"?`${p.value}%`:`GHS ${p.value}`} off · Used {p.times_used}×
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop */}
              <div className="fin-desktop-table table-wrap">
                <table>
                  <thead><tr><th>Code</th><th>Type</th><th>Value</th><th>Times Used</th><th>Total Discount Given</th></tr></thead>
                  <tbody>
                    {promos.map((p, i) => (
                      <tr key={i}>
                        <td><span className="mono" style={{ color:"var(--accent)", fontWeight:700 }}>{p.code}</span></td>
                        <td style={{ textTransform:"capitalize", color:"var(--text2)" }}>{p.type}</td>
                        <td className="mono">{p.type === "percentage" ? `${p.value}%` : `GHS ${p.value}`}</td>
                        <td className="mono">{p.times_used}</td>
                        <td className="mono" style={{ color:"var(--red)", fontWeight:700 }}>- GHS {fmt(p.total_discount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Payout Modal */}
      {payoutModal && (
        <div className="modal-overlay" onClick={() => setPayoutModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Record Payout</div>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setPayoutModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color:"var(--text2)", fontSize:13, marginBottom:16 }}>
                Recording payout for <strong style={{ color:"var(--text)" }}>{payoutModal.rider_name}</strong>
                <span style={{ fontSize:11, color:"var(--text3)", marginLeft:8 }}>rider_id: {payoutModal.rider_id}</span>
              </p>
              <div className="form-group">
                <label className="form-label">Amount (GHS)</label>
                <input className="input" type="number" step="0.01" value={payoutAmt} onChange={e => setPayoutAmt(e.target.value)}/>
              </div>
              <div className="form-group">
                <label className="form-label">Note (optional)</label>
                <input className="input" placeholder="e.g. Weekly payout via MoMo" value={payoutNote} onChange={e => setPayoutNote(e.target.value)}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setPayoutModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handlePayout} disabled={paying || !payoutAmt}>
                {paying ? "Saving..." : `Confirm GHS ${payoutAmt} Payout`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}